<template>
    <div v-if="modelValue" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
        <div class="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-auto">
            <div class="p-6 border-b border-gray-200">
                <h2 class="text-3xl font-bold text-center mb-2">Congratulations!</h2>
                <h3 class="text-xl text-center">You crossed the finish line <span v-if="gamePosition">in <strong>{{ getOrdinalSuffix(gamePosition) }}</strong> place </span>with a time of <strong>{{ secondsToDurationString(finishTime) }}</strong>. Below is an overview of your race:</h3>
            </div>

            <div class="flex flex-col lg:flex-row h-[70vh]">
                <!-- Left Column - Leaderboard -->
                <div class="lg:w-5/12 overflow-y-auto border-r border-gray-200">
                    <div class="p-6 pb-0">
                        <h4 class="text-lg font-semibold mb-4">Final Standings</h4>
                    </div>
                    <div class="overflow-hidden rounded-lg">
                        <table class="w-full bg-white">
                            <thead>
                                <tr>
                                    <th class="px-4 py-3 text-left text-sm font-semibold tracking-wider"></th>
                                    <th class="px-4 py-3 text-left text-sm font-semibold tracking-wider">Player</th>
                                    <th class="px-4 py-3 text-left text-sm font-semibold tracking-wider">Time</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr
                                    v-for="entry in leaderboard"
                                    :key="entry.id"
                                    class="text-gray-900"
                                    :class="{
                                        'bg-yellow-50': entry.position === 1,
                                        'bg-gray-50': entry.position === 2,
                                        'bg-orange-50': entry.position === 3,
                                        'font-bold text-black bg-gray-100': entry.id === gameId
                                    }"
                                >
                                    <td class="px-4 py-3 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <span
                                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                                :class="{
                                                    'bg-yellow-100 text-yellow-800': entry.position === 1,
                                                    'bg-gray-100 text-gray-800': entry.position === 2,
                                                    'bg-orange-100 text-orange-800': entry.position === 3,
                                                    'bg-gray-200 text-gray-800': entry.position > 3
                                                }"
                                            >
                                                {{ getOrdinalSuffix(entry.position) }}
                                            </span>
                                            <span v-if="entry.position === 1" class="ml-2">🏆</span>
                                        </div>
                                    </td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm">{{ entry.first_name }}</td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm font-mono">{{ entry.formatted_time }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Right Column - Route Map -->
                <div class="lg:w-7/12 p-6 overflow-y-auto">
                    <h4 class="text-lg font-semibold mb-4">Your Route</h4>
                    <div class="h-1/2 bg-gray-100 rounded-lg relative mb-6">
                        <div v-if="loadingRoute" class="absolute inset-0 flex items-center justify-center">
                            <div class="text-gray-500">Loading route...</div>
                        </div>
                        <div ref="routeMapContainer" class="w-full h-full rounded-lg"></div>
                    </div>

                    <!-- Journey Table -->
                    <div v-if="journeySteps.length > 0">
                        <h5 class="text-lg font-semibold mb-3">Journey Details</h5>
                        <div class="overflow-hidden rounded-lg border border-gray-200">
                            <table class="w-full bg-white text-sm">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th class="px-3 py-2 text-left font-semibold text-gray-700">#</th>
                                        <th class="px-3 py-2 text-left font-semibold text-gray-700">Transport</th>
                                        <th class="px-3 py-2 text-left font-semibold text-gray-700">From</th>
                                        <th class="px-3 py-2 text-left font-semibold text-gray-700">To</th>
                                        <th class="px-3 py-2 text-left font-semibold text-gray-700">Cost</th>
                                        <th class="px-3 py-2 text-left font-semibold text-gray-700">Time</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-200">
                                    <tr v-for="(step, index) in journeySteps" :key="index" class="hover:bg-gray-50">
                                        <td class="px-3 py-2 text-gray-600">{{ index + 1 }}</td>
                                        <td class="px-3 py-2">
                                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                                                  :class="getTransportColor(step.event)">
                                                {{ step.event }}
                                            </span>
                                        </td>
                                        <td class="px-3 py-2 text-gray-700">{{ step.from }}</td>
                                        <td class="px-3 py-2 text-gray-700">{{ step.to }}</td>
                                        <td class="px-3 py-2 text-gray-700">
                                            <span v-if="step.cost" class="font-mono">{{ step.cost }}</span>
                                            <span v-else class="text-gray-400">-</span>
                                        </td>
                                        <td class="px-3 py-2 text-gray-600 font-mono text-xs">{{ step.time }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="p-6 border-t border-gray-200 text-center">
                <p class="mb-4 text-xl">Share your result with all your mates and challenge them to beat your time!</p>
                <div class="flex gap-4 justify-center">
                    <Button
                        class="relative px-12 py-3 text-white bg-green-500 hover:bg-green-600 rounded transition"
                        @click="shareRaceResult"
                        :disabled="!gamePosition"
                    >
                        Share my result
                        <span class="absolute inset-0 flex items-center justify-center text-white bg-green-500 transition-opacity duration-300" :class="{'opacity-100': copied, 'opacity-0': !copied}">Copied</span>
                    </Button>
                    <Button
                        class="px-12 py-3 text-white bg-blue-500 hover:bg-blue-600 rounded transition"
                        @click="resetGame"
                    >
                        Start New Game
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, shallowRef, computed, nextTick } from 'vue';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Button from '@/components/Button.vue';
import { useGameStore } from '@/stores/useGameStore.js'
import { api } from '@/utils/api';
import { getOrdinalSuffix } from '@/utils/string';
import { secondsToDurationString } from '@/utils/time';

const props = defineProps({
    saved: { type: Boolean, default: false },
    modelValue: {
        type: Boolean,
        default: false
    },
    finishTime: {
        type: [String, Number],
        required: true
    },
    gameId: {
        type: [String, Number],
        required: true
    },
    raceId: {
        type: [String, Number],
        required: true
    }
});

const emit = defineEmits(['update:modelValue', 'share-result']);

const gameStore = useGameStore()

const routeMapContainer = ref(null);
const routeMap = shallowRef(null);
const loadingRoute = ref(false);
const gamePosition = ref(null);
const leaderboard = ref([]);
const routeLogs = ref([]);
const copied = ref(false);

// Mapbox token - you might want to move this to environment variables
// Resolve token from Vite env (VITE_MAPBOX_TOKEN). Keep fallback to the previous token.
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1IjoicGF0dGxlIiwiYSI6ImNtaTM5dGJiZTBnMW8ya3F2ZDJma2FwNTcifQ.MjNHplZhL2bi7jTIMlMnGg';

// Computed property to format journey steps
const journeySteps = computed(() => {
    const steps = [];
    
    routeLogs.value.forEach(log => {
        // Skip ping events and non-transport events
        if (log.event === 'Ping' || log.event === 'Finish') return;
        
        let from = 'Unknown Location';
        let to = 'Unknown Location';
        
        // Handle different event types
        if (log.train) {
            from = log.train.from_station_name || 'Unknown Station';
            to = log.train.to_station_name || 'Unknown Station';
        } else {
            // For other events, try to get location names or use coordinates
            from = getLocationName(log.from_lat, log.from_lng);
            to = getLocationName(log.to_lat, log.to_lng);
        }
        
        const cost = log.money ? `${log.money} ${log.currency}` : null;
        const time = formatGameTime(Math.max(0, Number(log.start) - Number(gameStore.race.start_time)));
        
        steps.push({
            event: log.event,
            from,
            to,
            cost,
            time,
            timestamp: log.start
        });
    });
    
    // Sort by timestamp to ensure chronological order
    return steps.sort((a, b) => a.timestamp - b.timestamp);
});

// Helper function to get location name (simplified - you might want to enhance this)
const getLocationName = (lat, lng) => {
    if (!lat || !lng) return 'Unknown';
    
    // You could implement reverse geocoding here or use a lookup table
    // For now, just return truncated coordinates
    return `${parseFloat(lat).toFixed(3)}, ${parseFloat(lng).toFixed(3)}`;
};

// Helper function to format game time
const formatGameTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '';
    
    const days = Math.floor(seconds / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
    } else {
        return `${minutes}m`;
    }
};

// Helper function to get transport type color
const getTransportColor = (event) => {
    const colors = {
        'Train': 'bg-blue-100 text-blue-800',
        'Train Ticket': 'bg-blue-100 text-blue-800',
        'Bus': 'bg-green-100 text-green-800',
        'Bus Ticket': 'bg-green-100 text-green-800',
        'Boat': 'bg-cyan-100 text-cyan-800',
        'Boat Ticket': 'bg-cyan-100 text-cyan-800',
        'Taxi': 'bg-yellow-100 text-yellow-800',
        'Walk': 'bg-gray-100 text-gray-800',
        'Flight': 'bg-purple-100 text-purple-800',
        'Flight Ticket': 'bg-purple-100 text-purple-800'
    };
    
    return colors[event] || 'bg-gray-100 text-gray-800';
};

function fetchRaceLeaderboard() {
    api({
        method: 'GET',
        endpoint: `api/races/${props.raceId}/leaderboard`,
        params: {},
        successCallback: (apiResponse) => {
            leaderboard.value = apiResponse.data.data;
        },
    })
}

function fetchUserRaceLeaderboard() {
    api({
        method: 'GET',
        endpoint: `api/races/${props.raceId}/games/${props.gameId}`,
        params: {},
        successCallback: (apiResponse) => {
            gamePosition.value = apiResponse.data.data?.race_rank ?? null;
        },
    })
}

const fetchGameLogs = async () => {
    if (!props.gameId) return;
    
    loadingRoute.value = true;
    try {
        api({
            method: 'GET',
            endpoint: `api/games/${props.gameId}/logs`,
            params: {},
            successCallback: (apiResponse) => {
                routeLogs.value = apiResponse.data.data;
                displayRouteOnMap();
            },
        })
    } catch (error) {
        console.error('Error fetching game logs:', error);
    } finally {
        loadingRoute.value = false;
    }
};

const displayRouteOnMap = () => {
    if (!routeMap.value || !routeLogs.value.length) return;

    // Extract coordinates from logs
    const coordinates = [];
    const waypoints = [];

    routeLogs.value.forEach(log => {
        if (log.event === 'Ping') return; // Skip ping events
        if (log.from_lat && log.from_lng) {
            const coord = [parseFloat(log.from_lng), parseFloat(log.from_lat)];
            coordinates.push(coord);

            // Add waypoint for significant events
            if (['Train Ticket', 'Bus Ticket', 'Boat Ticket', 'Taxi', 'Walk'].includes(log.event)) {
                waypoints.push({
                    coordinates: coord,
                    event: log.event,
                    details: log
                });
            }
        }
        
        // Also add destination if different from origin
        if (log.to_lat && log.to_lng && 
            (log.to_lat !== log.from_lat || log.to_lng !== log.from_lng)) {
            const coord = [parseFloat(log.to_lng), parseFloat(log.to_lat)];
            coordinates.push(coord);
        }
    });

    // Remove duplicate coordinates
    const uniqueCoordinates = coordinates.filter((coord, index, arr) => {
        return index === 0 || 
            (coord[0] !== arr[index-1][0] || coord[1] !== arr[index-1][1]);
    });

    if (uniqueCoordinates.length === 0) return;

    // Add route line
    if (routeMap.value.getSource('route')) {
        routeMap.value.removeLayer('route-line');
        routeMap.value.removeSource('route');
    }

    routeMap.value.addSource('route', {
        type: 'geojson',
        data: {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: uniqueCoordinates
            }
        }
    });

    routeMap.value.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
            'line-join': 'round',
            'line-cap': 'round'
        },
        paint: {
            'line-color': '#2563eb',
            'line-width': 4
        }
    });

    // Add waypoint markers
    waypoints.forEach((waypoint, index) => {
        const markerElement = document.createElement('div');
        markerElement.className = 'route-waypoint';
        markerElement.style.cssText = `
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background-color: #dc2626;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        `;

        const popup = new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
                <div>
                    <strong>${waypoint.event}</strong><br>
                    ${waypoint.details.train?.from_station_name || 'Location'}
                    ${waypoint.details.money ? `<br>Cost: ${waypoint.details.money} ${waypoint.details.currency}` : ''}
                </div>
            `);

        new mapboxgl.Marker(markerElement)
            .setLngLat(waypoint.coordinates)
            .setPopup(popup)
            .addTo(routeMap.value);
    });

    // Fit map to show entire route
    const bounds = new mapboxgl.LngLatBounds();
    uniqueCoordinates.forEach(coord => bounds.extend(coord));
    routeMap.value.fitBounds(bounds, { padding: 50 });
};

const initializeRouteMap = () => {
    if (!routeMapContainer.value) return;

    routeMap.value = new mapboxgl.Map({
        container: routeMapContainer.value,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-0.1278, 51.5074], // Default to London
        zoom: 6
    });

    routeMap.value.on('load', () => {
        if (routeLogs.value.length > 0) {
            displayRouteOnMap();
        }
    });
};

const shareRaceResult = () => {
    copied.value = true;

    setTimeout(() => {
        copied.value = false;
    }, 5000);

    emit('share-result', {
        position: getOrdinalSuffix(gamePosition.value),
        time: secondsToDurationString(props.finishTime)
    });
};

function resetGame() {
    gameStore.resetGame({}, () => {
        window.location.reload();
    });
}

const close = () => {
    emit('update:modelValue', false);
};

watch(() => props.modelValue, async (newValue) => {
    if (newValue) {
        await nextTick();
        initializeRouteMap();
    }
});

watch(() => props.modelValue && props.saved, ready => {
    if (!ready) return;
    fetchGameLogs();
    fetchRaceLeaderboard();
    fetchUserRaceLeaderboard();
});

onUnmounted(() => {
    if (routeMap.value) {
        routeMap.value.remove();
    }
});
</script>

<style scoped>
/* Ensure the map container has proper sizing */
.route-waypoint {
    cursor: pointer;
}

/* Make sure the map fills its container properly */
:deep(.mapboxgl-map) {
    border-radius: 0.5rem;
}
</style>
